/**
 * lib/phone.ts — country-code-aware phone validation, shared by the checkout
 * phone field (client) and the order/subscription routes (server).
 *
 * A bare "9987749950" passed validation before this existed — there was no
 * country context, so any 8-15 digit string looked plausible. Real per-country
 * validation (correct digit count, valid prefixes) needs a country selected
 * alongside the national number, which is what CountryCodeSelect + this module
 * are for. Numbers are always stored/sent as E.164 (e.g. "+919987749950").
 */

import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js'

export interface CountryOption {
  code: CountryCode
  name: string
  dialCode: string
  flag: string
}

/** 🇮🇳 from "IN" — regional indicator symbols, no flag asset/library needed. */
function flagEmoji(countryCode: string): string {
  return countryCode
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
    .join('')
}

let _countryOptions: CountryOption[] | null = null

/** All countries libphonenumber-js knows how to validate, sorted by name. India first. */
export function countryOptions(): CountryOption[] {
  if (_countryOptions) return _countryOptions

  const displayNames = new Intl.DisplayNames(['en'], { type: 'region' })
  const options = getCountries().map((code) => ({
    code,
    name: displayNames.of(code) ?? code,
    dialCode: `+${getCountryCallingCode(code)}`,
    flag: flagEmoji(code),
  }))

  options.sort((a, b) => {
    if (a.code === 'IN') return -1
    if (b.code === 'IN') return 1
    return a.name.localeCompare(b.name)
  })

  _countryOptions = options
  return options
}

/** True if `nationalNumber` is a valid number for `country` (correct length + prefix rules). */
export function isValidNationalPhone(nationalNumber: string, country: CountryCode): boolean {
  return isValidPhoneNumber(nationalNumber.trim(), country)
}

/** Combine a country + national number into E.164 (e.g. "+919987749950"), or null if invalid. */
export function toE164(nationalNumber: string, country: CountryCode): string | null {
  const parsed = parsePhoneNumberFromString(nationalNumber.trim(), country)
  return parsed && parsed.isValid() ? parsed.number : null
}

/**
 * Server-side check that a phone string is a genuinely valid, fully-qualified
 * E.164 number (must include the leading "+"). Used to validate what the
 * client sends after combining country + national number via toE164() —
 * never trust the client's own validation.
 */
export function isValidE164(value: string): boolean {
  return value.trim().startsWith('+') && isValidPhoneNumber(value.trim())
}

export type { CountryCode }
